import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Clock, Users, Flag, CheckCircle2, XCircle, Lock, Bot } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PersonaHealthCard, MockAuditCard } from "./PersonaReadinessCards";

const TONE = { verified: "text-emerald-300", failed: "text-rose-300", needs_review: "text-sky-300", pending: "text-amber-300", expired: "text-zinc-400", none: "text-zinc-500" };
const LABEL = { verified: "Verified 18+", failed: "Failed", needs_review: "Manual review", pending: "Pending", expired: "Expired", none: "Not started" };
const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

function Stat({ k, n, Icon, onClick, active }) {
  return (
    <button type="button" onClick={onClick} className={`text-left rounded-xl border p-3 transition-colors ${active ? "border-rose-500/50 bg-rose-500/10" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`} data-testid={`guardian-stat-${k}`}>
      <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wider ${TONE[k] || "text-zinc-400"}`}><Icon size={12} /> {LABEL[k] || k}</div>
      <div className="text-2xl font-semibold text-white mt-1">{n}</div>
    </button>
  );
}

function DecideRow({ item, onDone }) {
  const [note, setNote] = useState("");
  const decide = async (decision) => {
    try { await api.post(`/admin/verification/${item.user_id}/decide`, { decision, note }); toast.success(decision === "verified" ? "Marked Verified 18+" : "Marked Failed"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Decision failed"); }
  };
  const underage = item.underage_lock || item.reason === "under_18";
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2" data-testid={`guardian-queue-item-${item.user_id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold text-white">{item.display_name || item.handle || item.user_id}</span>
        <span className="text-xs text-zinc-500">@{item.handle}</span>
        <span className={`text-xs ${TONE[item.status]}`}>{LABEL[item.status]}</span>
        {item.reason && <span className="text-[11px] text-zinc-400 bg-white/5 rounded-full px-2 py-0.5">{item.reason}</span>}
        <span className="text-[11px] text-zinc-500 ml-auto">attempts {item.attempts} · {fmt(item.updated_at)}</span>
      </div>
      {item.appeal && <div className="text-xs text-sky-100 bg-sky-500/10 border border-sky-400/20 rounded-lg p-2" data-testid="guardian-appeal-text">Member: “{item.appeal.message}” <span className="text-sky-300/70">({fmt(item.appeal.created_at)})</span></div>}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Review note (no document details)" className="h-8 text-xs bg-black/40 border-white/10 max-w-xs" data-testid="guardian-note" />
        <Button size="sm" className="h-8 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white" disabled={underage} title={underage ? "Under-18 results cannot be overridden" : ""} onClick={() => decide("verified")} data-testid="guardian-approve"><CheckCircle2 size={14} className="mr-1" /> Verified 18+</Button>
        <Button size="sm" variant="outline" className="h-8 rounded-full border-rose-500/40 text-rose-200" onClick={() => decide("failed")} data-testid="guardian-reject"><XCircle size={14} className="mr-1" /> Failed</Button>
        {underage && <span className="text-[11px] text-rose-300 flex items-center gap-1"><Lock size={11} /> under-18 lock</span>}
      </div>
    </div>
  );
}

export default function AgeVerificationPanel() {
  const [sum, setSum] = useState(null);
  const [queue, setQueue] = useState({ items: [], flags: [] });
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const load = useCallback(async () => {
    const [{ data: s }, { data: qd }, { data: l }] = await Promise.all([
      api.get("/admin/verification/summary"), api.get("/admin/verification/queue"), api.get("/admin/verification/list", { params: { status, q: q || undefined } }),
    ]);
    setSum(s); setQueue(qd); setList(l.items);
  }, [status, q]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const resolveFlag = async (id) => { await api.post(`/admin/verification/flags/${id}/resolve`); load(); };
  if (!sum) return <div className="text-sm text-zinc-500" data-testid="guardian-loading">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="guardian-age-verification">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2"><Bot size={18} className="text-rose-400" /><h2 className="text-lg font-semibold text-white">Panda Guardian · Age Verification</h2></div>
        <span className="text-[11px] rounded-full px-2 py-0.5 border border-white/10 text-zinc-400" data-testid="guardian-mode">Persona {sum.mode === "live" ? `live · ${sum.key_kind || ""} key` : sum.mode === "mock" ? "simulator (testers only)" : "disabled"}</span>
        <span className={`text-[11px] rounded-full px-2 py-0.5 border ${sum.required ? "border-rose-500/40 text-rose-300" : "border-white/10 text-zinc-400"}`} data-testid="guardian-enforcement">Enforcement {sum.required ? "ON" : "OFF"}</span>
        <span className="text-[11px] rounded-full px-2 py-0.5 border border-white/10 text-zinc-400">Prompt {sum.prompt ? "ON" : "OFF"}</span>
        <span className="text-[11px] text-zinc-500 ml-auto">{sum.total_members} members · min age {sum.min_age}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {[["none", Users], ["pending", Clock], ["verified", ShieldCheck], ["failed", ShieldAlert], ["needs_review", ShieldQuestion], ["expired", Clock]].map(([k, I]) => (
          <Stat key={k} k={k} n={sum.counts[k]} Icon={I} active={status === k} onClick={() => setStatus(status === k ? "all" : k)} />
        ))}
      </div>
      <p className="text-[11px] text-zinc-500 flex items-center gap-1"><Lock size={11} /> Guardian only flags and queues. It never approves an under-18 result and has no access to private rooms, private lives or DMs. MyPandaX stores no ID images, selfies or biometrics.</p>

      <PersonaHealthCard />
      <MockAuditCard onChanged={load} />

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">Review queue <span className="text-xs text-zinc-500">({queue.items.length} · {sum.open_appeals} appeals)</span></h3>
        {queue.items.length === 0 && <div className="text-xs text-zinc-500" data-testid="guardian-queue-empty">Nothing waiting for a human.</div>}
        {queue.items.map((it) => <DecideRow key={it.user_id} item={it} onDone={load} />)}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Flag size={14} className="text-amber-300" /> Guardian flags <span className="text-xs text-zinc-500">({queue.flags.length} open)</span></h3>
        {queue.flags.length === 0 && <div className="text-xs text-zinc-500" data-testid="guardian-flags-empty">No suspicious activity flagged.</div>}
        {queue.flags.map((f) => (
          <div key={f.id} className="flex flex-wrap items-center gap-2 text-xs rounded-lg border border-amber-400/20 bg-amber-500/5 px-3 py-2" data-testid={`guardian-flag-${f.kind}`}>
            <span className="font-semibold text-amber-200">{f.kind}</span><span className="text-zinc-300">{f.detail}</span>
            {f.user_id && <span className="text-zinc-400">{f.handle ? `@${f.handle}` : `user ${f.user_id.slice(0, 8)}…`}</span>}{f.count > 1 && <span className="text-zinc-500">×{f.count}</span>}<span className="text-zinc-600 ml-auto">{fmt(f.created_at)}</span>
            <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => resolveFlag(f.id)} data-testid="guardian-flag-resolve">Resolve</Button>
          </div>
        ))}
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-white">Members {status !== "all" && <span className="text-xs text-zinc-400">· {LABEL[status]}</span>}</h3>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / handle / email" className="h-8 text-xs bg-black/40 border-white/10 max-w-xs ml-auto" data-testid="guardian-search" />
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs" data-testid="guardian-member-table">
            <thead className="text-zinc-500 bg-white/[0.03]"><tr>{["Member", "Status", "Reason", "Attempts", "Mode", "Updated", "Decided by"].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-zinc-500">No verification records{status !== "all" ? " with this status" : ""}.</td></tr>}
              {list.map((r) => (
                <tr key={r.user_id} className="border-t border-white/5" data-testid={`guardian-row-${r.user_id}`}>
                  <td className="px-3 py-2 text-white">{r.display_name || r.handle}<span className="text-zinc-500"> @{r.handle}</span></td>
                  <td className={`px-3 py-2 ${TONE[r.status]}`}>{LABEL[r.status]}</td>
                  <td className="px-3 py-2 text-zinc-400">{r.reason || "—"}</td>
                  <td className="px-3 py-2 text-zinc-400">{r.attempts}</td>
                  <td className="px-3 py-2 text-zinc-500">{r.mode}</td>
                  <td className="px-3 py-2 text-zinc-500">{fmt(r.updated_at)}</td>
                  <td className="px-3 py-2 text-zinc-500">{r.review?.decided_by_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
